/*
  AC Offer engine (TGTHR). No dependencies.

  1. Scheduler: every [data-ac-offer-scope] gets data-ac-state = before | live | after from
     the real clock, re-checked every second, so offers open and close on their own.
     Children with data-ac-show="before live" are shown only in those states and
     [data-ac-countdown] counts down to the start (before) or end (live).
     A scope either carries its own data-ac-start / data-ac-end, or data-ac-offer-ref="<offer id>"
     to borrow the schedule printed by that offer's buy box (blank ref = first offer on the page).
  2. Fields: [data-ac-field="pack_price|compare_price|per_tee_price|savings|pack_size|offer_name"]
     are filled from the buy box config; [data-ac-requires="<field>"] hides when that field is blank.
  3. Buy box: validates the picker, adds one cart line per tee via ACOffer.addPackToCart, opens the cart.
     With "show_pack_quantity" on, [data-ac-pack-qty] picks how many identical packs to add (each its own pack id).
  4. Tracking: offer_view / offer_add_to_cart to dataLayer, Meta (fbq) and Klaviyo, only if present.

  QA: ?offer_preview=before|live|after forces every scope on the page into that state. It is
  honored only in the theme editor or on an unpublished theme preview (Shopify.theme.role is not
  'main'), so shoppers on the live theme cannot use it.
*/
(function () {
  'use strict';
  if (window.ACOffer) return;

  var STATES = ['before', 'live', 'after'];
  var designMode = Boolean(window.Shopify && window.Shopify.designMode);

  // Only on non-published themes or in the editor, never for shoppers on the live theme.
  var previewAllowed = designMode || Boolean(window.Shopify && window.Shopify.theme && window.Shopify.theme.role && window.Shopify.theme.role !== 'main');

  var previewParam = (function () {
    if (!previewAllowed) return null;
    try {
      var value = new URLSearchParams(window.location.search).get('offer_preview');
      return STATES.indexOf(value) > -1 ? value : null;
    } catch (e) {
      return null;
    }
  })();

  function editorState() {
    if (!designMode) return null;
    var el = document.querySelector('[data-ac-editor-state]');
    var value = el && el.getAttribute('data-ac-editor-state');
    return STATES.indexOf(value) > -1 ? value : null;
  }

  function parseTime(iso) {
    if (!iso) return null;
    var t = Date.parse(iso);
    return isNaN(t) ? null : t;
  }

  function norm(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  /* ---------- Offer configs (printed by each buy box) ---------- */

  var configCache = null;

  function readConfigs() {
    if (configCache) return configCache;
    var list = [];
    document.querySelectorAll('script[data-ac-offer-config]').forEach(function (el) {
      try {
        list.push(JSON.parse(el.textContent));
      } catch (e) {
        /* malformed config: ignore, the buy box shows its own editor note */
      }
    });
    configCache = list;
    return list;
  }

  function getConfig(ref) {
    var configs = readConfigs();
    if (!configs.length) return null;
    if (!ref) return configs[0];
    for (var i = 0; i < configs.length; i++) {
      if (norm(configs[i].offer_id) === norm(ref)) return configs[i];
    }
    return null;
  }

  function refOf(el) {
    var holder = el.closest('[data-ac-offer-ref]');
    return holder ? holder.getAttribute('data-ac-offer-ref') : '';
  }

  /* ---------- Scheduler ---------- */

  function scopeTimes(scope) {
    if (scope.hasAttribute('data-ac-offer-ref')) {
      var cfg = getConfig(scope.getAttribute('data-ac-offer-ref'));
      if (!cfg) return null;
      return { start: parseTime(cfg.start), end: parseTime(cfg.end) };
    }
    return { start: parseTime(scope.getAttribute('data-ac-start')), end: parseTime(scope.getAttribute('data-ac-end')) };
  }

  function computeState(scope, times, now) {
    // The header link is always shown inside the theme editor so it can be edited.
    if (scope.hasAttribute('data-ac-design')) return 'live';
    var forced = previewParam || editorState();
    if (forced) return forced;
    if (times.start && now < times.start) return 'before';
    if (times.end && now >= times.end) return 'after';
    return 'live';
  }

  function owned(scope, selector) {
    return Array.prototype.filter.call(scope.querySelectorAll(selector), function (el) {
      return el.closest('[data-ac-offer-scope]') === scope;
    });
  }

  function pad(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function renderCountdown(scope, state, times, now) {
    owned(scope, '[data-ac-countdown]').forEach(function (box) {
      var target = state === 'before' ? times.start : state === 'live' ? times.end : null;
      // A forced preview has no real target, so count to a fixed 2 days to show the layout.
      if (!target && (previewParam || editorState()) && state !== 'after') target = now + 172800000;
      if (!target || target <= now) {
        box.hidden = true;
        return;
      }
      var left = Math.floor((target - now) / 1000);
      var parts = {
        d: String(Math.floor(left / 86400)),
        h: pad(Math.floor((left % 86400) / 3600)),
        m: pad(Math.floor((left % 3600) / 60)),
        s: pad(left % 60),
      };
      box.querySelectorAll('[data-ac-unit]').forEach(function (unit) {
        var key = unit.getAttribute('data-ac-unit');
        if (unit.textContent !== parts[key]) unit.textContent = parts[key];
      });
      box.hidden = false;
    });
  }

  function applyState(scope, state) {
    var previous = scope.getAttribute('data-ac-state');
    owned(scope, '[data-ac-show]').forEach(function (el) {
      var shows = (el.getAttribute('data-ac-show') || '').split(/\s+/);
      el.hidden = shows.indexOf(state) === -1;
    });
    if (previous !== state || !scope.__acStateApplied) {
      scope.__acStateApplied = true;
      scope.setAttribute('data-ac-state', state);
      scope.dispatchEvent(
        new CustomEvent('ac-offer:state', { bubbles: true, detail: { state: state, previous: previous } })
      );
    }
  }

  function tick() {
    var now = Date.now();
    document.querySelectorAll('[data-ac-offer-scope]').forEach(function (scope) {
      var times = scopeTimes(scope);
      if (!times) return;
      var state = computeState(scope, times, now);
      applyState(scope, state);
      renderCountdown(scope, state, times, now);
    });
  }

  /* ---------- Fields ---------- */

  function hydrateFields(root) {
    (root || document).querySelectorAll('[data-ac-field], [data-ac-requires], [data-ac-plural]').forEach(function (el) {
      var cfg = getConfig(refOf(el));
      var display = (cfg && cfg.display) || {};
      var field = el.getAttribute('data-ac-field');
      if (field) el.textContent = display[field] || '';
      // data-ac-plural: singular or plural unit label for the pack size ("1 tee", "2 tees").
      if (el.hasAttribute('data-ac-plural')) {
        el.textContent = el.getAttribute(display.pack_size === '1' ? 'data-one' : 'data-many') || el.textContent;
      }
      var requires = el.getAttribute('data-ac-requires');
      if (requires) el.hidden = !(cfg && display[requires]);
    });
  }

  /* ---------- Header link ---------- */

  function syncHeaderLink(scope, state) {
    var template = scope.querySelector('template[data-ac-header-link-template]');
    var placement = scope.getAttribute('data-ac-placement') || 'nav_start';
    if (!template || placement === 'bar') return;
    var show = state === 'live';
    var key = scope.getAttribute('data-ac-header-link');
    var existing = document.querySelectorAll('[data-ac-header-link-item="' + key + '"]');
    if (!show) {
      existing.forEach(function (el) {
        el.remove();
      });
      return;
    }
    if (existing.length) return;
    document.querySelectorAll('.tg-header__nav-links').forEach(function (list) {
      var item = template.content.firstElementChild.cloneNode(true);
      item.setAttribute('data-ac-header-link-item', key);
      if (placement === 'nav_end') list.appendChild(item);
      else list.insertBefore(item, list.firstChild);
    });
  }

  document.addEventListener('ac-offer:state', function (event) {
    var scope = event.target;
    if (scope && scope.hasAttribute && scope.hasAttribute('data-ac-header-link')) syncHeaderLink(scope, event.detail.state);
  });

  /* ---------- Tracking ---------- */

  function track(names, payload, props) {
    if (designMode) return;
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push(Object.assign({ event: names.dataLayer }, payload));
    } catch (e) {
      /* never block the shopper on analytics */
    }
    try {
      if (typeof window.fbq === 'function') window.fbq('trackCustom', names.meta, props);
    } catch (e) {
      /* ignore */
    }
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') {
        window.klaviyo.push(['track', names.klaviyo, props]);
      } else if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['track', names.klaviyo, props]);
      }
    } catch (e) {
      /* ignore */
    }
  }

  function trackView(cfg, state) {
    var value = cfg.pack_price_cents / 100;
    var quantity = cfg.total_tees || cfg.pack_size;
    track(
      { dataLayer: 'offer_view', meta: 'OfferView', klaviyo: 'Viewed Offer' },
      {
        offer_id: cfg.offer_id,
        offer_name: cfg.offer_name,
        offer_state: state,
        value: value,
        currency: cfg.currency,
        items: [
          {
            item_id: String(cfg.product && cfg.product.id),
            item_name: cfg.offer_name,
            price: cfg.per_tee_cents / 100,
            quantity: quantity,
          },
        ],
        ecommerce: {
          currency: cfg.currency,
          value: value,
          items: [
            {
              item_id: String(cfg.product && cfg.product.id),
              item_name: cfg.offer_name,
              price: cfg.per_tee_cents / 100,
              quantity: quantity,
            },
          ],
        },
      },
      { offer_id: cfg.offer_id, offer_name: cfg.offer_name, offer_state: state, value: value, currency: cfg.currency }
    );
  }

  function trackAdd(cfg, lines, packId) {
    var value = cfg.pack_price_cents / 100;
    var items = lines.map(function (line) {
      return {
        item_id: String(cfg.product.id),
        item_name: cfg.product.title,
        item_variant: [line.color, line.size].filter(Boolean).join(' / '),
        variant_id: String(line.variantId),
        free: Boolean(line.free),
        price: line.free ? 0 : (cfg.free_count > 0 ? cfg.paid_tee_cents : cfg.per_tee_cents) / 100,
        quantity: 1,
      };
    });
    track(
      { dataLayer: 'offer_add_to_cart', meta: 'OfferAddToCart', klaviyo: 'Added Offer to Cart' },
      {
        offer_id: cfg.offer_id,
        offer_name: cfg.offer_name,
        pack_id: packId,
        value: value,
        currency: cfg.currency,
        items: items,
        ecommerce: { currency: cfg.currency, value: value, items: items },
      },
      {
        offer_id: cfg.offer_id,
        offer_name: cfg.offer_name,
        pack_id: packId,
        value: value,
        currency: cfg.currency,
        items: items.map(function (i) {
          return i.item_variant;
        }),
      }
    );
  }

  /* ---------- Cart ---------- */

  function cartUrl(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '') + path;
  }

  function postJSON(path, body) {
    return fetch(cartUrl(path), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then(function (response) {
      return response.json().then(function (data) {
        if (!response.ok || data.status) {
          var error = new Error(data.description || data.message || 'Cart error');
          error.shopperMessage = data.description || data.message;
          throw error;
        }
        return data;
      });
    });
  }

  function makePackId() {
    return Math.random().toString(36).slice(2, 8) + Date.now().toString(36);
  }

  /*
    THE ONLY function that writes the pack to the cart. Swap this body when the bundle
    mechanism is decided (e.g. add one native-bundle variant instead of N lines); callers
    pass the same arguments and expect a full cart object back.

    lines: [{ variantId, color, size, free }]   one entry per tee (free = build a bundle free step)
    meta:  { offerId, offerName, packLabel, perTeeCents, paidTeeCents, image }
    packs: how many copies of the pack to add (default 1); each copy gets its own _pack_id / cart group.
    Resolves { cart, packId, packIds }.
    _offer_price_each: paid tees carry the paid price (pack / paid tees), free tees 0.00;
    packs without free tees carry pack / tees.
  */
  function addPackToCart(lines, meta, packs) {
    var count = Math.max(1, Math.floor(Number(packs) || 1));
    var hasFree = lines.some(function (line) {
      return line.free;
    });
    var eachCents = hasFree && typeof meta.paidTeeCents === 'number' ? meta.paidTeeCents : meta.perTeeCents;
    var perTee = typeof eachCents === 'number' ? (eachCents / 100).toFixed(2) : '';
    var packIds = [];
    var items = [];
    for (var p = 0; p < count; p++) {
      var packId = makePackId() + (count > 1 ? String(p + 1) : '');
      packIds.push(packId);
      var base = {
        _offer_id: meta.offerId,
        _offer_name: meta.offerName,
        _pack_id: packId,
        _pack_size: String(lines.length),
        _offer_price_each: perTee,
        // Existing theme hook (assets/prebuilt-bundles-cart.js): groups these lines in the
        // Rebuy cart drawer as one pack row with this name.
        _bundle_group: packId,
        _bundle_name: meta.packLabel || meta.offerName,
      };
      try {
        localStorage.setItem('tgthr_bundle_' + packId, JSON.stringify({ name: base._bundle_name, image: meta.image || '' }));
      } catch (e) {
        /* storage blocked: the cart shows the lines ungrouped */
      }
      lines.forEach(function (line, index) {
        // _pack_slot keeps two identical tees (same color + size) as two separate lines.
        var properties = Object.assign({}, base, { _pack_slot: String(index + 1) });
        if (line.free) {
          properties._free_slot = 'true';
          properties._offer_price_each = '0.00';
        }
        items.push({ id: Number(line.variantId), quantity: 1, properties: properties });
      });
    }
    return postJSON('/cart/add.js', { items: items })
      .then(function () {
        // Order-level marker as well, so the order's "Additional details" shows the offer.
        return postJSON('/cart/update.js', { attributes: { _offer_id: meta.offerId } }).catch(function () {
          return null;
        });
      })
      .then(function () {
        return fetch(cartUrl('/cart.js'), { headers: { Accept: 'application/json' } }).then(function (r) {
          return r.json();
        });
      })
      .then(function (cart) {
        return { cart: cart, packId: packIds[0], packIds: packIds };
      });
  }

  function openCart(cart) {
    try {
      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'ac-offer', cartData: cart });
      }
    } catch (e) {
      /* ignore */
    }
    if (typeof window.openRebuyCartOrRedirect === 'function') {
      window.openRebuyCartOrRedirect(cart);
    } else {
      window.location.href = (window.routes && window.routes.cart_url) || '/cart';
    }
  }

  /* ---------- Buy box ---------- */

  function BuyBox(root) {
    this.root = root;
    var cfgEl = root.querySelector('script[data-ac-offer-config]');
    try {
      this.cfg = JSON.parse(cfgEl.textContent);
    } catch (e) {
      this.cfg = null;
    }
    if (!this.cfg) return;
    this.button = root.querySelector('[data-ac-add]');
    this.errorEl = root.querySelector('[data-ac-error]');
    this.slots = Array.prototype.slice.call(root.querySelectorAll('[data-ac-slot]'));
    this.shared = root.querySelector('[data-ac-shared-size]');
    this.state = root.getAttribute('data-ac-state') || 'live';
    this.busy = false;
    this.qtyInput = root.querySelector('[data-ac-qty-input]');
    this.qtyMax = Math.max(1, Number(this.cfg.pack_quantity_max) || 1);

    var self = this;
    if (this.qtyInput) {
      root.querySelectorAll('[data-ac-qty-step]').forEach(function (button) {
        button.addEventListener('click', function () {
          self.setPackCount(self.packCount() + Number(button.getAttribute('data-ac-qty-step')));
        });
      });
      this.qtyInput.addEventListener('input', function () {
        if (self.qtyInput.value !== '') self.setPackCount(self.qtyInput.value, true);
      });
      this.qtyInput.addEventListener('blur', function () {
        self.setPackCount(self.qtyInput.value);
      });
      this.setPackCount(1);
    }
    root.addEventListener('change', function (event) {
      var target = event.target;
      if (target.hasAttribute('data-ac-qty-input')) return;
      var picker = target.closest('details[data-ac-select]');
      if (picker) picker.open = false;
      if (target.hasAttribute('data-ac-individual-toggle')) {
        self.setIndividual(target.checked);
        self.showError('');
        return;
      }
      if (target.hasAttribute('data-ac-apply-size')) {
        self.applySizeToAll(target.value);
        return;
      }
      var slot = target.closest('[data-ac-slot]');
      if (target.hasAttribute('data-ac-color-input')) self.syncSlot(slot);
      if (self.shared) self.syncShared();
      if (
        !self.isIndividual() &&
        target.hasAttribute('data-ac-size-input') &&
        target.closest('[data-ac-shared-size]')
      ) {
        self.applySizeToAll(target.value);
      }
      self.updateSummary(slot);
      self.syncPickers();
      self.showError('');
    });
    root.addEventListener('ac-offer:state', function (event) {
      if (event.target !== root) return;
      self.state = event.detail.state;
      self.updateButton();
      self.maybeTrackView();
    });
    var applySize = root.querySelector('select[data-ac-apply-size]');
    if (applySize) applySize.addEventListener('change', function () {
      self.applySizeToAll(applySize.value);
    });
    if (this.button) this.button.addEventListener('click', function () {
      self.submit();
    });

    this.slots.forEach(function (slot) {
      self.syncSlot(slot);
      self.updateSummary(slot);
    });
    if (this.shared) this.syncShared();
    this.individualToggle = root.querySelector('[data-ac-individual-toggle]');
    if (this.individualToggle) this.setIndividual(this.individualToggle.checked);
    this.syncPickers();
    this.updateButton();
    this.maybeTrackView();
  }

  // offer_view fires once, and only while the offer is live (also when it flips live on the page).
  BuyBox.prototype.maybeTrackView = function () {
    if (this.viewTracked || this.state !== 'live' || this.cfg.coming_soon) return;
    this.viewTracked = true;
    trackView(this.cfg, this.state);
  };

  BuyBox.prototype.applySizeToAll = function (size) {
    if (!size) return;
    var self = this;
    this.slots.forEach(function (slot) {
      var select = slot.querySelector('select[data-ac-size-select]');
      if (select) {
        var option = Array.prototype.filter.call(select.options, function (o) {
          return o.value === size && !o.disabled;
        })[0];
        if (option) select.value = size;
      } else {
        slot.querySelectorAll('input[data-ac-size-input]').forEach(function (input) {
          if (input.value === size && !input.disabled) input.checked = true;
        });
      }
      self.updateSummary(slot);
    });
    this.syncPickers();
    this.showError('');
  };

  /* r3 dropdowns use the product page markup: <details data-ac-select> with radio options.
     Keep each trigger's text (and color swatch) in step with the checked radio. */
  function syncPicker(details) {
    var checked = details.querySelector('input[type="radio"]:checked');
    var text = details.querySelector('.js-selected-text');
    if (text) text.textContent = checked ? checked.value : text.getAttribute('data-placeholder') || '';
    var holder = details.querySelector('.js-selected-swatch');
    if (holder) {
      var source = checked && checked.parentNode.querySelector('.buy-box__custom-select-option-swatch .swatch');
      holder.innerHTML = '';
      if (source) holder.appendChild(source.cloneNode(true));
      holder.hidden = !source;
    }
  }

  function markDisabled(input) {
    var label = input.closest('.buy-box__custom-select-option-label');
    if (label) label.classList.toggle('disabled', input.disabled);
  }

  BuyBox.prototype.syncPickers = function () {
    this.root.querySelectorAll('details[data-ac-select]').forEach(syncPicker);
  };

  /* Mystery: "Black:2, Heather Gray:1" plus optional "S=Black:2,Heather Gray:2; M=..." per size. */
  function parseCombo(text) {
    var out = [];
    String(text || '')
      .split(',')
      .forEach(function (part) {
        var bits = part.split(':');
        var color = (bits[0] || '').trim();
        if (!color) return;
        var count = bits.length > 1 ? parseInt(bits[1], 10) : 1;
        if (!(count > 0)) count = 1;
        out.push({ color: color, count: count });
      });
    return out;
  }

  BuyBox.prototype.comboFor = function (size) {
    var mystery = this.cfg.mystery || {};
    var overrides = String(mystery.overrides || '').split(';');
    for (var i = 0; i < overrides.length; i++) {
      var eq = overrides[i].indexOf('=');
      if (eq < 0) continue;
      if (norm(overrides[i].slice(0, eq)) === norm(size)) return parseCombo(overrides[i].slice(eq + 1));
    }
    return parseCombo(mystery.combo);
  };

  BuyBox.prototype.mysteryAvailable = function (size) {
    var self = this;
    var combo = this.comboFor(size);
    return (
      combo.length > 0 &&
      combo.every(function (entry) {
        return self.sizeAvailable(entry.color, size);
      })
    );
  };

  BuyBox.prototype.slotColor = function (slot) {
    if (!this.cfg.product.has_color) return null;
    var fixed = slot.getAttribute('data-ac-fixed-color');
    if (fixed) return fixed;
    var single = slot.querySelector('[data-ac-color-single]');
    if (single) return single.value;
    var checked = slot.querySelector('input[data-ac-color-input]:checked');
    return checked ? checked.value : '';
  };

  BuyBox.prototype.isIndividual = function () {
    var toggle = this.root.querySelector('[data-ac-individual-toggle]');
    return Boolean(toggle && toggle.checked);
  };

  BuyBox.prototype.sharedSizeValue = function () {
    if (!this.shared) return '';
    var checked = this.shared.querySelector('input[data-ac-size-input]:checked');
    return checked ? checked.value : '';
  };

  BuyBox.prototype.clearSlotSizes = function () {
    this.slots.forEach(function (slot) {
      slot.querySelectorAll('input[data-ac-size-input]').forEach(function (input) {
        input.checked = false;
      });
      var select = slot.querySelector('select[data-ac-size-select]');
      if (select) select.value = '';
    });
  };

  /* Shared size is the default. Ticking the checkbox prefills each tee with that size
     (skipping a size the tee's color does not have). Unticking copies it back. */
  BuyBox.prototype.setIndividual = function (on) {
    this.root.setAttribute('data-individual-sizes', on ? 'true' : 'false');
    var heading = this.root.querySelector('[data-ac-rows-heading]');
    if (heading) {
      var next = heading.getAttribute(on ? 'data-heading-individual' : 'data-heading-shared');
      if (next) heading.textContent = next;
    }
    this.clearSlotSizes();
    var size = this.sharedSizeValue();
    if (size) {
      this.applySizeToAll(size);
      return;
    }
    var self = this;
    this.slots.forEach(function (slot) {
      self.updateSummary(slot);
    });
    this.syncPickers();
  };

  BuyBox.prototype.slotSize = function (slot) {
    if (!this.cfg.product.has_size) return null;
    var useSlot = !this.shared || this.isIndividual();
    if (useSlot && slot) {
      var select = slot.querySelector('select[data-ac-size-select]');
      if (select) return select.value;
      if (slot.querySelector('input[data-ac-size-input]')) {
        var checked = slot.querySelector('input[data-ac-size-input]:checked');
        return checked ? checked.value : '';
      }
    }
    var scope = this.shared || slot;
    if (!scope) return '';
    var sharedChecked = scope.querySelector('input[data-ac-size-input]:checked');
    if (sharedChecked) return sharedChecked.value;
    var sharedSelect = scope.querySelector('select[data-ac-size-select]');
    return sharedSelect ? sharedSelect.value : '';
  };

  BuyBox.prototype.findVariant = function (color, size, availableOnly) {
    var variants = this.cfg.product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      var v = variants[i];
      if (color !== null && norm(v.color) !== norm(color)) continue;
      if (size !== null && size !== undefined && norm(v.size) !== norm(size)) continue;
      if (availableOnly && !v.available) continue;
      return v;
    }
    return null;
  };

  BuyBox.prototype.sizeAvailable = function (color, size) {
    return Boolean(this.findVariant(color, size, true));
  };

  // Disable sizes that have no available variant in the slot's color.
  BuyBox.prototype.syncSlot = function (slot) {
    if (!slot) return;
    var self = this;
    var color = this.slotColor(slot);
    slot.querySelectorAll('input[data-ac-size-input]').forEach(function (input) {
      input.disabled = !self.sizeAvailable(color, input.value);
      if (input.disabled && input.checked) input.checked = false;
      markDisabled(input);
    });
    slot.querySelectorAll('select[data-ac-size-select] option').forEach(function (option) {
      if (!option.value) return;
      option.disabled = !self.sizeAvailable(color, option.value);
      if (option.disabled && option.selected) option.parentNode.value = '';
    });
    var thumb = slot.querySelector('[data-ac-slot-thumb]');
    var withImage = color !== null ? this.findVariantWithImage(color) : null;
    if (thumb) {
      if (withImage) {
        thumb.src = withImage.image;
        thumb.hidden = false;
      } else {
        thumb.hidden = true;
      }
    }
  };

  BuyBox.prototype.findVariantWithImage = function (color) {
    var variants = this.cfg.product.variants || [];
    for (var i = 0; i < variants.length; i++) {
      if (norm(variants[i].color) === norm(color) && variants[i].image) return variants[i];
    }
    return null;
  };

  // One size for the pack: a size is only offered if every tee's color has it.
  BuyBox.prototype.syncShared = function () {
    var self = this;
    if (this.cfg.build_type === 'mystery') {
      this.shared.querySelectorAll('input[data-ac-size-input]').forEach(function (input) {
        input.disabled = !self.mysteryAvailable(input.value);
        if (input.disabled && input.checked) input.checked = false;
        markDisabled(input);
      });
      return;
    }
    var colors = this.slots.map(function (slot) {
      return self.slotColor(slot);
    });
    this.shared.querySelectorAll('input[data-ac-size-input]').forEach(function (input) {
      input.disabled = !colors.every(function (color) {
        return self.sizeAvailable(color, input.value);
      });
      if (input.disabled && input.checked) input.checked = false;
      markDisabled(input);
    });
  };

  BuyBox.prototype.updateSummary = function (slot) {
    if (!slot || slot.hasAttribute('data-ac-fixed-color')) return;
    var summary = slot.querySelector('[data-ac-slot-summary]');
    if (!summary) return;
    var sizePart = this.shared && !this.isIndividual() ? null : this.slotSize(slot);
    var parts = [this.slotColor(slot), sizePart].filter(Boolean);
    summary.textContent = parts.length ? ': ' + parts.join(', ') : '';
  };

  BuyBox.prototype.packCount = function () {
    if (!this.qtyInput) return 1;
    var n = Math.floor(Number(this.qtyInput.value));
    return isNaN(n) ? 1 : Math.min(this.qtyMax, Math.max(1, n));
  };

  // keepTyping: clamp without rewriting the field mid-keystroke unless the value is out of range.
  BuyBox.prototype.setPackCount = function (value, keepTyping) {
    if (!this.qtyInput) return;
    var raw = Math.floor(Number(value));
    var n = isNaN(raw) ? 1 : Math.min(this.qtyMax, Math.max(1, raw));
    if (!keepTyping || n !== raw) this.qtyInput.value = String(n);
    var minus = this.root.querySelector('[data-ac-qty-step="-1"]');
    var plus = this.root.querySelector('[data-ac-qty-step="1"]');
    if (minus) minus.disabled = n <= 1;
    if (plus) plus.disabled = n >= this.qtyMax;
    var total = this.root.querySelector('[data-ac-qty-total]');
    if (total) {
      var tees = (Number(this.cfg.total_tees) || 0) * n;
      var text = '';
      if (n > 1) {
        text = tees + ' tees';
        if (typeof this.cfg.pack_price_cents === 'number' && this.cfg.pack_price_cents > 0) {
          try {
            text += ', ' + new Intl.NumberFormat(undefined, { style: 'currency', currency: this.cfg.currency || 'USD' })
              .format((this.cfg.pack_price_cents * n) / 100).replace(/\.00(?=\D*$)/, '') + ' total';
          } catch (e) {
            /* no Intl: show the tee count only */
          }
        }
      }
      total.textContent = text;
    }
    this.updateButton();
  };

  BuyBox.prototype.updateButton = function () {
    if (!this.button) return;
    var live = this.state === 'live';
    var label = this.button.querySelector('[data-ac-add-label]');
    var text = this.button.getAttribute(live ? 'data-label-live' : 'data-label-before') || '';
    var packs = this.packCount();
    if (live && packs > 1) {
      // "Add 2 tees to cart" -> "Add 4 tees to cart"; labels without the tee count get " (x2)".
      var tees = String(this.cfg.total_tees);
      var re = new RegExp('(^|\\D)' + tees + '(?= tees?\\b)', 'i');
      text = re.test(text)
        ? text.replace(re, function (m, pre) { return pre + String(Number(tees) * packs); })
        : text + ' (x' + packs + ')';
    }
    if (label) label.textContent = text;
    var disabled = !live || this.busy;
    this.button.disabled = disabled;
    if (disabled) this.button.setAttribute('aria-disabled', 'true');
    else this.button.removeAttribute('aria-disabled');
  };

  BuyBox.prototype.showError = function (message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || '';
    this.errorEl.hidden = !message;
  };

  BuyBox.prototype.message = function (key, slot, size) {
    var strings = this.cfg.strings || {};
    var name = (slot && slot.getAttribute('data-ac-slot-name')) || '';
    return String(strings[key] || '')
      .replace('[item]', name)
      .replace('[size]', size || '');
  };

  BuyBox.prototype.collectMystery = function () {
    var size = this.slotSize(null);
    if (!size) return { error: this.message('choose_size_shared') };
    if (!this.mysteryAvailable(size)) return { error: this.message('mystery_unavailable', null, size) };
    var lines = [];
    var self = this;
    this.comboFor(size).forEach(function (entry) {
      var variant = self.findVariant(entry.color, size, true);
      for (var n = 0; n < entry.count; n++) lines.push({ variantId: variant.id, color: variant.color, size: size });
    });
    return { lines: lines };
  };

  BuyBox.prototype.collectLines = function () {
    if (this.cfg.build_type === 'mystery') return this.collectMystery();
    var lines = [];
    for (var i = 0; i < this.slots.length; i++) {
      var slot = this.slots[i];
      var color = this.slotColor(slot);
      var size = this.slotSize(slot);
      if (color === '') return { error: this.message('choose_color', slot) };
      if (size === '') return { error: this.message(!this.shared || this.isIndividual() ? 'choose_size' : 'choose_size_shared', slot) };
      var variant = this.findVariant(color, size, false);
      if (!variant) return { error: this.message('unavailable', slot) };
      lines.push({ variantId: variant.id, color: color, size: size, free: slot.hasAttribute('data-ac-free') });
    }
    return { lines: lines };
  };

  BuyBox.prototype.submit = function () {
    if (this.busy || this.state !== 'live' || this.cfg.coming_soon) return;
    this.showError('');
    var result = this.collectLines();
    if (result.error) {
      this.showError(result.error);
      return;
    }
    var self = this;
    var cfg = this.cfg;
    this.busy = true;
    this.button.classList.add('loading');
    this.updateButton();
    addPackToCart(
      result.lines,
      {
        offerId: cfg.offer_id,
        offerName: cfg.offer_name,
        packLabel: cfg.cart_pack_label,
        perTeeCents: cfg.per_tee_cents,
        paidTeeCents: cfg.paid_tee_cents,
        image: cfg.product.image,
      },
      this.packCount()
    )
      .then(function (added) {
        added.packIds.forEach(function (packId) {
          trackAdd(cfg, result.lines, packId);
        });
        openCart(added.cart);
      })
      .catch(function (error) {
        self.showError((error && error.shopperMessage) || self.message('error'));
      })
      .then(function () {
        self.busy = false;
        self.button.classList.remove('loading');
        self.updateButton();
      });
  };

  /* Dropdowns: one open at a time, close on an outside click or Escape, mirror aria-expanded. */
  document.addEventListener('click', function (event) {
    document.querySelectorAll('details[data-ac-select][open]').forEach(function (details) {
      if (!details.contains(event.target)) details.open = false;
    });
  });
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('details[data-ac-select][open]').forEach(function (details) {
      details.open = false;
      var summary = details.querySelector('summary');
      if (summary) summary.focus();
    });
    document.querySelectorAll('.buy-box__info-wrapper').forEach(function (wrapper) {
      var popover = wrapper.querySelector('.buy-box__info-popover');
      if (!popover || popover.hidden) return;
      popover.hidden = true;
      var toggle = wrapper.querySelector('[data-ac-info-toggle]');
      if (toggle) {
        toggle.setAttribute('aria-expanded', 'false');
        toggle.focus();
      }
    });
  });

  /* Same (i) popover behavior as the main theme buy box. */
  document.addEventListener('click', function (event) {
    var clickedToggle = event.target.closest && event.target.closest('[data-ac-info-toggle]');
    document.querySelectorAll('.buy-box__info-wrapper').forEach(function (wrapper) {
      var popover = wrapper.querySelector('.buy-box__info-popover');
      if (popover && !popover.hidden && !wrapper.contains(event.target)) {
        popover.hidden = true;
        var toggle = wrapper.querySelector('[data-ac-info-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }
    });
    if (!clickedToggle) return;
    var wrapper = clickedToggle.closest('.buy-box__info-wrapper');
    var popover = wrapper && wrapper.querySelector('.buy-box__info-popover');
    if (!popover) return;
    var willShow = popover.hidden;
    popover.hidden = !willShow;
    clickedToggle.setAttribute('aria-expanded', willShow ? 'true' : 'false');
  });
  document.addEventListener(
    'toggle',
    function (event) {
      var details = event.target;
      if (!details.matches || !details.matches('details[data-ac-select]')) return;
      var summary = details.querySelector('summary');
      if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    },
    true
  );

  /* ---------- Boot ---------- */

  function initBuyBoxes(root) {
    (root || document).querySelectorAll('[data-ac-offer-buybox]').forEach(function (el) {
      if (el.__acBuyBox) return;
      el.__acBuyBox = new BuyBox(el);
    });
  }

  function boot(root) {
    configCache = null;
    hydrateFields(root);
    tick();
    initBuyBoxes(root);
  }

  window.ACOffer = {
    version: '1.3.0',
    addPackToCart: addPackToCart,
    track: track,
    getConfig: getConfig,
    refresh: function () {
      boot(document);
    },
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }
  setInterval(tick, 1000);

  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
  });
})();
